import { Injectable, signal } from '@angular/core';
import { EarlyAccessFeature } from '@app/app/components/settings/early-access/early-access-feature';
import {
  LendioAngularMaterialThemeService
} from '@app/app/components/lendio-angular-material-theme/lendio-angular-material-theme.service';
import cookies from '@app/app/libs/cookieManager';

@Injectable({
  providedIn: 'root',
})
export class EarlyAccessFeatureService {
  private _features: EarlyAccessFeature[] = [
    {
      id: 1,
      name: 'opt-in',
      type: 'recommended',
      heading: 'Opt in to everything new',
      description: 'Always enroll me in what\'s new',
      subtexts: [
        ' When enabled, you’re opting to always see new updates' +
        ' and features when you sign in. Selecting this option will' +
        ' select all options below by default.'
      ],
      enabled: false,
    },
    {
      id: 2,
      name: 'new-theme',
      type: 'new',
      heading: 'Introducing a brand new interface.',
      description: 'Enable new interface',
      subtexts: [
        'Get better insights, more customizations, and new visualizations.',
        'Opt in to the new look now, or continue with the classic theme.'
      ],
      enabled: false
    }
  ];
  private _optInFeatureIndex = this._features.findIndex(feature => feature.name === 'opt-in');
  private _themeFeatureIndex = this._features.findIndex(feature => feature.name === 'new-theme');
  public earlyAccessFeatures = signal(this._features);
  public earlyAccessShowBadge = signal(false);
  public earlyAccessShowDialog = signal(false);

  constructor(private _themeService: LendioAngularMaterialThemeService) {
    this._themeService.enabled.subscribe(themeEnabled => {
      this._features[this._themeFeatureIndex].enabled = themeEnabled;
    });
    this._initEarlyAccessSettingsCookie();
  }

  private _initEarlyAccessSettingsCookie(): void {
    const settingsCookie = cookies.get('lendioEarlyAccessSettings');
    if (!settingsCookie) {
      cookies.set('lendioEarlyAccessSettings', JSON.stringify({
        isEnrolledAll: false,
        showNewFeatureBadge: false,
        showNewFeatureDialog: true,
      }));
      // todo change these back to 'true' when transition changes are complete
      this.earlyAccessShowBadge.set(false);
      this.earlyAccessShowDialog.set(false);
    } else {
      const settings = JSON.parse(cookies.get('lendioEarlyAccessSettings'));
      // todo remove setting to false and uncomment setting to existing settings.show values when transition changes are complete
      this.earlyAccessShowBadge.set(false);
      this.earlyAccessShowDialog.set(false);
      // this.earlyAccessShowBadge.set(settings.showNewFeatureBadge);
      // this.earlyAccessShowDialog.set(settings.showNewFeatureDialog);
      this._features[this._optInFeatureIndex].enabled = settings.isEnrolledAll;
    }
  }

  private _setOptInCookie(state: boolean): void {
    let settingsCookie = cookies.get('lendioEarlyAccessSettings');
    if (settingsCookie) {
      let settings = JSON.parse(settingsCookie);
      settings.isEnrolledAll = state;
      cookies.set('lendioEarlyAccessSettings', JSON.stringify(settings));
    }
  }

  public triggerFeatureUpdates(): void {
    // enable all
    if (this._features[this._optInFeatureIndex].enabled) {
      this._features.forEach(feature => feature.enabled = true);
      this._setOptInCookie(true);
    } else {
      this._setOptInCookie(false);
    }

    // change theme
    if (this._features[this._themeFeatureIndex]) {
      this._features[this._themeFeatureIndex].enabled
        ? this._themeService.enable()
        :  this._themeService.disable();
    }
  }

  public handleOptInEnabled(): void {
    if (this.earlyAccessFeatures()[this._optInFeatureIndex].enabled) {
      this._features.forEach(feature => feature.enabled = true);
    }
  }

  public disableNewFeatureBadge(): void {
    let settingsCookie = cookies.get('lendioEarlyAccessSettings');
    if (settingsCookie) {
      let settings = JSON.parse(settingsCookie);
      if (settings.showNewFeatureBadge) {
        settings.showNewFeatureBadge = false;
        cookies.set('lendioEarlyAccessSettings', JSON.stringify(settings));
        this.earlyAccessShowBadge.set(false);
      }
    }
  }

  public disableNewFeatureDialog(): void {
    let settingsCookie = cookies.get('lendioEarlyAccessSettings');
    if (settingsCookie) {
      let settings = JSON.parse(settingsCookie);
      if (settings.showNewFeatureModal) {
        settings.showNewFeatureDialog = false;
        cookies.set('lendioEarlyAccessSettings', JSON.stringify(settings));
        this.earlyAccessShowDialog.set(false);
      }
    }
  }
}
